import React, { useState, useEffect } from 'react';
import { X, Check, ArrowLeft } from 'lucide-react';

interface ImagePreviewModalProps {
  isOpen: boolean;
  imageUrl: string;
  fileName: string;
  onConfirm: () => void;
  onCancel: () => void;
  isUploading: boolean;
  title?: string;
}

const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({
  isOpen,
  imageUrl,
  fileName,
  onConfirm,
  onCancel,
  isUploading,
  title
}) => {
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);

  useEffect(() => {
    if (isOpen && imageUrl) {
      const img = document.createElement('img');
      img.onload = () => {
        setDimensions({ width: img.width, height: img.height });
      };
      img.onerror = () => {
        setDimensions(null);
      };
      img.src = imageUrl;
    } else {
      setDimensions(null);
    }
  }, [isOpen, imageUrl]);

  if (!isOpen) return null;

  const aspectRatio = dimensions ? (dimensions.width / dimensions.height).toFixed(2) : null;
  const isCoverImage = title?.toLowerCase().includes('cover');
  const expectedAspectRatio = 2 / 3; // 2:3 for cover images
  const aspectRatioValid = dimensions && isCoverImage 
    ? Math.abs(dimensions.width / dimensions.height - expectedAspectRatio) <= 0.05
    : true;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-apple p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{title || 'Preview Image'}</h3>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="mb-4">
          <div className="relative bg-gray-50 rounded-apple border border-gray-200 p-4 mb-3">
          <img
            src={imageUrl}
              alt={`${(title || 'Image')} preview`}
              className="w-full max-h-96 object-contain rounded border border-gray-300 bg-white mx-auto"
          />
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-gray-600 font-medium">File:</span>
              <span className="text-gray-900">{fileName}</span>
            </div>
            {dimensions && (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 font-medium">Dimensions:</span>
                  <span className="text-gray-900">
                    {dimensions.width} × {dimensions.height}px
                  </span>
                </div>
                {aspectRatio && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 font-medium">Aspect Ratio:</span>
                    <span className={`font-medium ${aspectRatioValid ? 'text-green-600' : 'text-orange-600'}`}>
                      {aspectRatio}:1
                    </span>
                  </div>
                )}
                {/* {isCoverImage && !aspectRatioValid && (
                  <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-800">
                    ⚠️ Cover image should have a 2:3 aspect ratio (recommended: 1200×1800px or 1600×2560px)
                  </div>
                )} */}
              </>
            )}
          </div>
        </div>
        
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-secondary"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isUploading}
            className="btn btn-primary"
          >
            {isUploading ? (
              'Uploading...'
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Confirm Upload
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImagePreviewModal;
